import classnames from 'classnames';
import qs from 'qs';
import dayjs from 'dayjs';
import {
  useDispatch,
  Dispatch,
  useSelector,
  State,
  useHistory,
} from '@medlinker/fundamental';
import React, { useCallback, useEffect, useState } from 'react';
import { PostCheckCreteEpc } from '../../../apis/prescription';
import useDidMount from '../../hooks/useDidMount';
// import usePageConfig from '../../hooks/usePageConfig';
import Toast from '../../components/Toast';
import env from '../../utils/env';
import Spinner from '../../components/Spinner';
import Modal from '../../components/Modal/Modal';
import * as eventTracker from '../../utils/EventTracker';
import PrescriptionCard from './components/PrescriptionCard';
import ListContainter from './components/ListContainter';
import DataListFooter from './components/DataListFooter';
import styles from './index.module.scss';

interface Props {
  doctorId: number;
  onClose: () => void;
}
const ChoosePrescriptionModal = (props: Props) => {
  const dispatch: Dispatch = useDispatch();
  const history = useHistory();
  const doctorId = props.doctorId;
  const [clicked, setClicked] = useState(false);
  // 选中处方id
  const [selectedId, setSelectedId] = useState<number>();
  // 展示模式 1-小弹窗 2-全屏，默认小弹窗
  const [mode, setMode] = useState(1);
  // 当前选择处方
  const [currentPre, setCurrentPre] = useState<any>();
  const [defaultId, setDefaultId] = useState<number>();

  // 处方列表
  const prescriptionList = useSelector(
    (state: State) => state.prQuickContinuation.prescriptionList
  );
  const start = useSelector((state: State) => state.prQuickContinuation.start);
  const limit = useSelector((state: State) => state.prQuickContinuation.limit);
  const hasMore = useSelector(
    (state: State) => state.prQuickContinuation.hasMore
  );
  const loading = useSelector(
    (state: State) => state.loading.effects.prQuickContinuation.loadPrescription
  );
  const isInit = useSelector(
    (state: State) => state.prQuickContinuation.isInit
  );

  // usePageConfig({ title: '选择开药处方' });

  useDidMount(() => {
    dispatch.prQuickContinuation.resetState();
    loadData(true);
  });

  const loadData = useCallback(
    (isInit = false) => {
      if (isInit) {
        dispatch.prQuickContinuation
          .loadPrescription({ start: 0, limit, doctorId })
          .then(() => {
            dispatch.prQuickContinuation.setIsInit(false);
          });
      } else if (hasMore) {
        dispatch.prQuickContinuation.loadPrescription({
          start,
          limit,
          doctorId,
        });
      }
    },
    [dispatch.prQuickContinuation, doctorId, hasMore, limit, start]
  );

  // 列表footer
  const renderFooter = useCallback(() => {
    if (prescriptionList.length === 0) {
      return;
    }

    return <DataListFooter loading={loading} hasMore={hasMore} />;
  }, [prescriptionList.length, loading, hasMore]);

  // 选中处方
  const handleSelect = useCallback(
    (prescriptionInfo: any) => {
      if (!clicked) {
        eventTracker.track('repurchaseThreePrescriptionModalChoose', {
          action:
            defaultId === prescriptionInfo?.electronic.id
              ? 'default'
              : 'notDefault',
          prescriptionId: prescriptionInfo?.electronic?.id.toString() ?? '',
        });
      }
      setClicked(true);

      setSelectedId(prescriptionInfo?.electronic.id);
      setCurrentPre(prescriptionInfo);
      setMode(1);
    },
    [clicked, defaultId]
  );

  // 续方
  const handleSendContinuation = useCallback(async () => {
    if (!selectedId) {
      Toast.info('请选择一个处方');

      return;
    }

    if (!clicked) {
      eventTracker.track('repurchaseThreePrescriptionModalConfirm', {
        prescriptionId: selectedId + '',
      });
    }
    setClicked(true);

    eventTracker.track('repurchaseThreePrescriptionModalRealConfirm', {
      prescriptionId: selectedId + '',
    });

    if (selectedId) {
      try {
        // 校验是否可续方
        await PostCheckCreteEpc().setData({ prescriptionId: selectedId });
      } catch (err) {
        // @ts-ignore
        Toast.info(err?.errmsg || '请求失败');

        return;
      }
    }

    // 爱康项目导医通app内需要给im额外传递一个参数（通过/consult/fill-patient页面透传）
    const appName = env.isDaoYiTongApp ? 'daoyitong' : '';

    setTimeout(() => {
      // 合规：随诊跳转完善就诊人
      history.push(
        `/consult/fill-patient${qs.stringify(
          {
            from: 'rePurchase',
            prescriptionId: selectedId,
            _fromPage: appName,
          },
          {
            addQueryPrefix: true,
          }
        )}`
      );

      window.location.reload();
    }, 100);
  }, [selectedId, clicked, history]);

  useEffect(() => {
    setSelectedId(prescriptionList[0]?.electronic.id);
    setCurrentPre(prescriptionList[0]);
    setDefaultId(prescriptionList[0]?.electronic.id);
  }, [prescriptionList]);

  useEffect(() => {
    if (mode === 1 && !loading && selectedId) {
      setClicked(false);
      eventTracker.track('repurchaseThreePrescriptionModal', {
        prescriptionId: selectedId?.toString() ?? '',
      });
    }
  }, [loading, mode, selectedId]);

  if (loading && mode === 1) {
    return <Spinner full />;
  }

  return mode === 1 ? (
    <Modal open={true} archor="bottom">
      <div className={styles.container}>
        <div className={styles.title}>
          确认处方
          <i
            className={styles.closeBtn}
            onClick={() => {
              if (!clicked && selectedId) {
                eventTracker.track('repurchaseThreePrescriptionModalCancel', {
                  prescriptionId: selectedId?.toString() ?? '',
                });
              }

              props.onClose();
            }}
          />
        </div>
        <div className={styles.prescriptionInfo}>
          <div className={styles.prescriptionHeader}>
            <div className={styles.prescriptionTitle}>
              处方笺
              <div className={styles.prescriptionCode}>
                处方编号：{currentPre?.electronic?.transNo}
              </div>
            </div>
            <div className={styles.prescriptionDate}>
              {dayjs.unix(currentPre?.electronic.createAt).format('YYYY/MM/DD')}
            </div>
          </div>
          <div className={styles.diagnose}>
            诊断：<span>{currentPre?.electronic?.diagnose}</span>
          </div>
          <div className={styles.body}>
            <div className={styles.rp}>RP</div>
            <div className={styles.durgs}>
              {currentPre?.drugs.map((drug: any) => (
                <div className={styles.drug} key={drug.contentId}>
                  <div className={styles.drugName}>{drug.title}</div>
                  <div className={styles.specWrap}>
                    <span className={styles.spec}>
                      规格: {drug.specification}
                    </span>
                    数量: ×{drug.quantity}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className={styles.prescriptionFooter}>
          <div
            className={classnames(styles.btn, styles.whiteBtn)}
            onClick={() => {
              if (!clicked && selectedId) {
                eventTracker.track('repurchaseThreePrescriptionModalChange', {
                  prescriptionId: selectedId?.toString() ?? '',
                });

                eventTracker.track('repurchaseThreePrescriptionModalList', {});
              }
              setClicked(true);
              setMode(2);
            }}
          >
            更换处方
          </div>
          <div className={styles.btn} onClick={handleSendContinuation}>
            提交
          </div>
        </div>
      </div>
    </Modal>
  ) : (
    <ListContainter
      onReachEnd={loadData}
      isInit={isInit}
      isEmpty={!prescriptionList.length}
      renderFooter={renderFooter}
      className={styles.list}
      doctorId={doctorId}
    >
      <>
        {prescriptionList.map((pre: any) => (
          <PrescriptionCard
            prescriptionInfo={pre}
            selectedPrescriptionId={selectedId}
            onSelect={handleSelect}
            key={pre.electronic.id}
          />
        ))}
      </>
    </ListContainter>
  );
};

export default ChoosePrescriptionModal;
